import type { PageType, SiteType } from "../schema/index.js";

export const CANONICAL_OUTPUT_KEYS = [
  "page",
  "funnel",
  "email",
  "lesson",
  "certificate",
  "display",
] as const;

export const CANONICAL_OUTPUT_CHANNELS = [
  "web",
  "email",
  "display",
] as const;

export const CANONICAL_OUTPUT_STATES = [
  "active",
  "planned",
] as const;

export const CANONICAL_OUTPUT_CAPABILITIES = [
  "analytics",
  "automation",
  "blog_content",
  "certificate_verification",
  "conversion_tracking",
  "course_progress",
  "custom_domain",
  "forms",
  "funnel_routing",
  "navigation",
  "seo",
  "site_shell",
  "theming",
] as const;

export const BLOCK_CAPABILITIES = [
  "content",
  "conversion",
  "commerce",
  "education",
  "embed",
  "form",
  "media",
  "navigation",
  "proof",
  "runtime",
  "tracking",
] as const;

export const BLOCK_OUTPUT_SUPPORT_LEVELS = [
  "full",
  "limited",
  "unsupported",
] as const;

export const BLOCK_OUTPUT_RESTRICTION_CODES = [
  "certificate_read_only",
  "course_context_only",
  "funnel_context_only",
  "interactive_runtime_only",
  "navigation_discouraged",
  "output_not_supported",
  "publish_runtime_only",
  "web_runtime_only",
] as const;

export const OUTPUT_RESOLUTION_SOURCES = [
  "output_key",
  "page_type",
  "site_type",
  "default",
] as const;

export type CanonicalOutputKey = (typeof CANONICAL_OUTPUT_KEYS)[number];
export type CanonicalOutputChannel = (typeof CANONICAL_OUTPUT_CHANNELS)[number];
export type CanonicalOutputState = (typeof CANONICAL_OUTPUT_STATES)[number];
export type CanonicalOutputCapability = (typeof CANONICAL_OUTPUT_CAPABILITIES)[number];
export type BlockCapability = (typeof BLOCK_CAPABILITIES)[number];
export type BlockOutputSupportLevel = (typeof BLOCK_OUTPUT_SUPPORT_LEVELS)[number];
export type BlockOutputRestrictionCode = (typeof BLOCK_OUTPUT_RESTRICTION_CODES)[number];
export type OutputResolutionSource = (typeof OUTPUT_RESOLUTION_SOURCES)[number];

export interface CanonicalOutputRestrictions {
  allowsAutomation: boolean;
  allowsCustomDomain: boolean;
  allowsForms: boolean;
  allowsIndexing: boolean;
  allowsNavigation: boolean;
  allowsSeo: boolean;
  allowsSiteShell: boolean;
  allowsTheming: boolean;
  supportsCourseProgress: boolean;
  supportsFunnelRouting: boolean;
  supportsVerification: boolean;
}

export interface CanonicalOutputCompatibility {
  currentPageTypes: readonly PageType[];
  currentSiteTypes: readonly SiteType[];
  defaultPageType?: PageType;
  defaultSiteType?: SiteType;
}

export interface CanonicalOutputContract {
  key: CanonicalOutputKey;
  label: string;
  description: string;
  channel: CanonicalOutputChannel;
  state: CanonicalOutputState;
  capabilities: readonly CanonicalOutputCapability[];
  restrictions: CanonicalOutputRestrictions;
  compatibility: CanonicalOutputCompatibility;
}

export interface ResolveOutputContractInput {
  outputKey?: CanonicalOutputKey;
  pageType?: PageType;
  siteType?: SiteType;
}

export interface ResolvedOutputContract {
  key: CanonicalOutputKey;
  source: OutputResolutionSource;
  contract: CanonicalOutputContract;
}

export interface BlockOutputSupport {
  outputKey: CanonicalOutputKey;
  level: BlockOutputSupportLevel;
  restrictionCode?: BlockOutputRestrictionCode;
  note?: string;
}

export interface BlockOutputContract {
  capabilities: readonly BlockCapability[];
  support: readonly BlockOutputSupport[];
}
